import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Icons,
  Modal,
  TextField,
  Typography,
  useTheme,
} from '@ublend-npm/aulaui-core';
import { Post } from '@app/types/core';
import { createTag, fetchTags } from '@core/actions/tags/tag';
import { createTagPost, deleteTagPost } from '@core/actions/tags/tagPost';
import {
  accessingCurrentSpaceAsStaff,
  isInstructor,
} from '@core/selectors/classroom';
import { currentUserId } from '@core/selectors/user';
import { AulaDispatch, AulaState } from '@core/types/state';
import { createStyles } from './ResponsivePostTags.styles';

type Tag = { objectId?: string; name: string };

const compareTags = (a: Tag, b: Tag) => a.name.localeCompare(b.name);
const sameTag = (a: Tag, b: Tag) =>
  (a.objectId && a.objectId === b.objectId) ||
  a.name.toLocaleLowerCase() === b.name.toLocaleLowerCase();

const ResponsivePostTags = ({ post }: { post: Post }) => {
  const dispatch: AulaDispatch = useDispatch();
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [draftTags, setDraftTags] = useState<Tag[]>([]);
  const [query, setQuery] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const { tags, spaceTags, canManage } = useSelector((state: AulaState) => {
    const data = state.data as any;
    const storedPost = data.posts[post.objectId] || post;
    const resolvedTags = (storedPost.tagPosts || [])
      .map((tagPost: { tagId: string }) => data.tags[tagPost.tagId])
      .filter(Boolean)
      .map((tag: Tag) => ({ objectId: tag.objectId, name: tag.name }))
      .sort(compareTags);
    const isReadOnlyUser = accessingCurrentSpaceAsStaff(state);
    const isAuthor = currentUserId(state) === storedPost.user.objectId;

    return {
      tags: resolvedTags as Tag[],
      spaceTags: Object.values(data.tags || {}) as Tag[],
      canManage: !isReadOnlyUser && (isInstructor(state) || isAuthor),
    };
  });

  const openTagModal = () => {
    // Take a snapshot when the modal opens. Re-reading `tags` on every render
    // would discard chips that have just been added to the draft.
    setDraftTags(tags);
    setQuery('');
    setIsOpen(true);
    // The responsive feed can be rendered without the legacy feed's preload.
    // Refresh the space catalogue so existing tags are always suggestible.
    dispatch(fetchTags(post.classRoom) as any).catch(() => undefined);
  };

  const suggestions = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    if (!normalizedQuery) return [];
    return spaceTags
      .filter(
        (tag) =>
          !draftTags.some((selected) => sameTag(selected, tag)) &&
          tag.name.toLocaleLowerCase().includes(normalizedQuery)
      )
      .sort(compareTags)
      .slice(0, 8);
  }, [draftTags, query, spaceTags]);

  const addTag = (tag: Tag) => {
    if (!draftTags.some((selected) => sameTag(selected, tag))) {
      setDraftTags((current) => [...current, tag].sort(compareTags));
    }
    setQuery('');
  };

  const addTypedTag = () => {
    const name = query.trim();
    if (!name) return;
    const matchingTag = spaceTags.find(
      (tag) => tag.name.toLocaleLowerCase() === name.toLocaleLowerCase()
    );
    addTag(matchingTag || { name });
  };

  const close = () => {
    if (!isSaving) {
      setIsOpen(false);
      setQuery('');
    }
  };

  const apply = async () => {
    setIsSaving(true);
    try {
      const tagsToRemove = tags.filter(
        (tag) => !draftTags.some((draftTag) => sameTag(draftTag, tag))
      );
      const tagsToAdd = draftTags.filter(
        (tag) => !tags.some((existingTag) => sameTag(existingTag, tag))
      );

      // Create and attach additions before removing existing tags, so a failed
      // save does not discard a user's current tags. The available API creates
      // one tag-post relationship at a time, so submit those operations
      // together when Apply is pressed.
      await Promise.all(
        tagsToAdd.map(async (tag) => {
          const persistedTag = tag.objectId
            ? tag
            : await (dispatch(
                createTag(tag.name, post.classRoom) as any
              ) as any);
          await (dispatch(
            createTagPost(persistedTag.objectId, post.objectId) as any
          ) as any);
        })
      );
      await Promise.all(
        tagsToRemove
          .filter((tag) => tag.objectId)
          .map((tag) =>
            dispatch(
              deleteTagPost({
                tagId: tag.objectId!,
                postId: post.objectId,
              }) as any
            )
          )
      );
      setIsOpen(false);
      setQuery('');
    } finally {
      setIsSaving(false);
    }
  };

  const inlineTags = isExpanded ? tags : tags.slice(0, 5);
  const hiddenCount = Math.max(tags.length - inlineTags.length, 0);
  const styles = createStyles(theme, tags.length > 0);
  const hasNewTag =
    query.trim() &&
    !spaceTags.some((tag) =>
      tag.name.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase())
    );

  return (
    <>
      <Box sx={styles.tagRow}>
        <Icons.TagOutlined aria-hidden="true" sx={styles.tagIcon} />
        <Box sx={styles.tagList}>
          {inlineTags.map((tag) => (
            <Chip
              key={tag.objectId}
              label={tag.name}
              kind="default"
              size="small"
              sx={{ ...styles.tagChip, ...styles.staticTagChip }}
            />
          ))}
          {hiddenCount > 0 ? (
            <Chip
              label={`+ ${hiddenCount}`}
              kind="default"
              size="small"
              onClick={() => setIsExpanded(true)}
              sx={{ ...styles.tagChip, ...styles.interactiveTagChip }}
            />
          ) : null}
          {isExpanded && tags.length > 5 ? (
            <Chip
              label="Show less"
              kind="default"
              size="small"
              onClick={() => setIsExpanded(false)}
              sx={{ ...styles.tagChip, ...styles.showLessChip }}
            />
          ) : null}
          {canManage ? (
            <Button
              kind="secondary"
              size="small"
              onClick={openTagModal}
              aria-haspopup="dialog"
              sx={styles.addTagButton}
            >
              + Add Tag
            </Button>
          ) : null}
        </Box>
      </Box>

      <Modal
        open={isOpen}
        onClose={close}
        aria-labelledby="add-post-tags-title"
      >
        <Box role="dialog" aria-modal="true" sx={styles.modal}>
          <Box sx={styles.modalHeader}>
            <Typography
              id="add-post-tags-title"
              component="h2"
              sx={styles.modalTitle}
            >
              Add tags
            </Typography>
            <IconButton
              aria-label="Close add tags"
              onClick={close}
              disabled={isSaving}
            >
              <Icons.Close />
            </IconButton>
          </Box>
          <Box sx={styles.searchContainer}>
            <TextField
              autoFocus
              aria-label="Search or add tags"
              aria-controls="tag-suggestions"
              aria-expanded={Boolean(query.trim())}
              label=""
              placeholder="Type here to add tags"
              value={query}
              sx={styles.searchField}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setQuery(event.target.value)
              }
              onKeyDown={(event: React.KeyboardEvent) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  if (suggestions[0]) addTag(suggestions[0]);
                  else addTypedTag();
                }
              }}
            />
            {query.trim() ? (
              <Box
                component="ul"
                id="tag-suggestions"
                role="listbox"
                aria-label="Tag suggestions"
                sx={styles.suggestionList}
              >
                {suggestions.map((tag, index) => (
                  <Box
                    component="li"
                    key={tag.objectId}
                    role="option"
                    tabIndex={0}
                    onClick={() => addTag(tag)}
                    onKeyDown={(event: React.KeyboardEvent) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        addTag(tag);
                      }
                    }}
                    sx={styles.suggestionItem(index === 0)}
                  >
                    <Typography component="span">{tag.name}</Typography>
                  </Box>
                ))}
                {hasNewTag ? (
                  <Box
                    component="li"
                    role="option"
                    tabIndex={0}
                    onClick={addTypedTag}
                    onKeyDown={(event: React.KeyboardEvent) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        addTypedTag();
                      }
                    }}
                    sx={styles.suggestionItem()}
                  >
                    <Typography component="span" sx={styles.newTagLabel}>
                      New tag:
                    </Typography>{' '}
                    <Typography component="span">{query.trim()}</Typography>
                  </Box>
                ) : null}
              </Box>
            ) : null}
          </Box>
          {draftTags.length > 0 ? (
            <>
              <Typography component="h3" sx={styles.selectedTagsTitle}>
                Tags:
              </Typography>
              <Box sx={styles.selectedTags}>
                {draftTags.map((tag) => (
                  <Chip
                    key={tag.objectId || tag.name}
                    label={tag.name}
                    kind="secondary"
                    size="small"
                    sx={styles.selectedTagChip}
                    onDelete={() =>
                      setDraftTags((current) =>
                        current.filter((item) => !sameTag(item, tag))
                      )
                    }
                  />
                ))}
              </Box>
            </>
          ) : null}
          <Box sx={styles.actions}>
            <Button kind="secondary" onClick={close} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              kind="primary"
              onClick={apply}
              disabled={isSaving}
              loading={isSaving}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default ResponsivePostTags;
